import { useState } from "react";
import { KeyRound, Loader2, AlertTriangle, ShieldCheck } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const MIN_LEN = 12;

// Used both as the forced first-sign-in screen (forced=true) and as the
// voluntary "change password" dialog from the account menu.
export default function ChangePassword({ forced = false, onDone, onCancel }) {
  const { changePassword, user, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("The two new passwords do not match.");
      return;
    }
    if (next.length < MIN_LEN) {
      setError(`Password must be at least ${MIN_LEN} characters.`);
      return;
    }
    setSubmitting(true);
    const res = await changePassword(current, next);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || "Could not change password.");
      return;
    }
    setDone(res.revoked || 0);
    if (!forced && onDone) onDone();
  };

  return (
    <div
      className="min-h-screen w-screen flex items-center justify-center bg-[#09090B] text-zinc-100 grid-bg p-6"
      data-testid="change-password-root"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <img src="/virtue-logo.png" alt="" className="h-5 w-auto block" />
          <span className="font-mono text-[11px] tracking-[0.28em] text-zinc-200 uppercase">
            Virtue Health
          </span>
        </div>

        <div className="border border-white/10 bg-[#0a0a0c]">
          <div className="px-6 pt-6 pb-5 border-b border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-amber-300">
                {forced ? "Action Required" : "Account Security"}
              </span>
            </div>
            <h1 className="font-display font-black text-3xl leading-[1] tracking-tight text-zinc-50 mb-2">
              SET A NEW PASSWORD
            </h1>
            <p className="text-[13px] leading-relaxed text-zinc-400">
              {forced
                ? "Your account is using a temporary password. Choose your own password to continue."
                : "Choose a new password. All your other signed-in devices will be signed out."}
            </p>
            {user?.email && (
              <p className="mt-2 font-mono text-[10px] tracking-[0.16em] text-zinc-600 uppercase">
                {user.email}
              </p>
            )}
          </div>

          {done !== null ? (
            <div className="p-6 space-y-4" data-testid="change-password-success">
              <p className="text-[13px] text-emerald-200 leading-relaxed">
                Password updated. {done} other session{done === 1 ? "" : "s"} signed out.
              </p>
              <button
                type="button"
                onClick={() => (forced ? window.location.reload() : onDone && onDone())}
                data-testid="change-password-continue"
                className="w-full px-4 py-2.5 border border-emerald-400/60 bg-emerald-400/10 hover:bg-emerald-400/20 font-mono text-[11px] tracking-[0.22em] uppercase text-emerald-200 transition-colors"
              >
                Continue →
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="p-6 space-y-4">
              {[
                {
                  id: "cp-current",
                  label: forced ? "Temporary Password" : "Current Password",
                  value: current,
                  set: setCurrent,
                  testid: "cp-current",
                  autoComplete: "current-password",
                },
                {
                  id: "cp-new",
                  label: `New Password (min ${MIN_LEN})`,
                  value: next,
                  set: setNext,
                  testid: "cp-new",
                  autoComplete: "new-password",
                },
                {
                  id: "cp-confirm",
                  label: "Confirm New Password",
                  value: confirm,
                  set: setConfirm,
                  testid: "cp-confirm",
                  autoComplete: "new-password",
                },
              ].map((f) => (
                <div key={f.id}>
                  <label
                    htmlFor={f.id}
                    className="block font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-1.5"
                  >
                    {f.label}
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                    <input
                      id={f.id}
                      type="password"
                      value={f.value}
                      autoComplete={f.autoComplete}
                      onChange={(e) => f.set(e.target.value)}
                      required
                      data-testid={f.testid}
                      className="w-full bg-[#09090B] border border-white/10 focus:border-emerald-400 outline-none pl-9 pr-3 py-2.5 font-mono text-[13px] text-zinc-100 placeholder:text-zinc-700 transition-colors"
                    />
                  </div>
                </div>
              ))}

              {error && (
                <div
                  className="flex items-start gap-2 border border-rose-400/30 bg-rose-400/5 px-3 py-2"
                  data-testid="change-password-error"
                >
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-400 mt-0.5 shrink-0" />
                  <span className="font-mono text-[11px] text-rose-200 leading-relaxed">{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !current || !next || !confirm}
                data-testid="change-password-submit"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-emerald-400/60 bg-emerald-400/10 hover:bg-emerald-400/20 disabled:opacity-50 disabled:cursor-not-allowed font-mono text-[11px] tracking-[0.22em] uppercase text-emerald-200 transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>Update Password →</>
                )}
              </button>

              <button
                type="button"
                onClick={() => (forced ? logout() : onCancel && onCancel())}
                data-testid="change-password-cancel"
                className="w-full px-4 py-2 border border-white/10 hover:border-white/25 font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                {forced ? "Sign out instead" : "Cancel"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
